/**
 * Validates barcode format for lot scanning
 * Must have exactly 2 pipes (||) OR exactly 2 tildes (~~) - not a mix
 * @param barcode - The barcode string to validate
 * @returns true if valid, false otherwise
 */
export const validateBarcodeFormat = (barcode: string): boolean => {
  const tildeCount = (barcode.match(/~/g) || []).length
  const pipeCount = (barcode.match(/\|/g) || []).length

  const hasExactlyTwoPipes = pipeCount === 2 && tildeCount === 0
  const hasExactlyTwoTildes = tildeCount === 2 && pipeCount === 0

  return hasExactlyTwoPipes || hasExactlyTwoTildes
}

/**
 * Result type for batch barcode validation
 */
export type BatchBarcodeValidationResult = {
  isValid: boolean
  correctedBarcode: string
}

/**
 * Validates barcode format for batch mode scanning
 * Rules:
 * 1. If barcode is 24 characters and all are numbers, it's valid
 * 2. If first 2 characters are "TS", it's valid (any length)
 * 3. If it has exactly 1 tilde (~), it's valid (more than 1 is invalid)
 * 4. If barcode contains 2 pipes (|):
 *    - Remove last pipe and everything after it, then check if it exists in actionIds
 *    - If not found, remove the last pipe again (from shortened string) and everything after it, then check again
 *    - If none match, it's invalid
 * 5. If barcode contains 1 pipe (|):
 *    - Check if full barcode exists in actionIds
 *    - If not, remove last pipe and everything after it, check again
 *    - If not found, it's invalid
 * @param barcode - The barcode string to validate
 * @param actionIds - Array of actionIds from all audit lots
 * @returns Object with isValid boolean and correctedBarcode string
 */
export const validateBarcodeFormatForBatch = (barcode: string, actionIds: string[]): BatchBarcodeValidationResult => {
  // Rule 1: 24 characters and all are numbers
  if (barcode.length <= 24 && /^\d+$/.test(barcode)) {
    return { isValid: true, correctedBarcode: barcode }
  }

  // Rule 2: First 2 characters are "TS"
  if (barcode.startsWith("TS")) {
    return { isValid: true, correctedBarcode: barcode }
  }

  // Rule 3: Exactly 1 tilde (~)
  const tildeCount = (barcode.match(/~/g) || []).length
  if (tildeCount === 1) {
    return { isValid: true, correctedBarcode: barcode }
  }

  // Rule 4: Handle pipes (|)
  const pipeCount = (barcode.match(/\|/g) || []).length

  if (pipeCount === 2) {
    // Strip first: Remove last pipe and everything after it, then compare
    const lastPipeIndex = barcode.lastIndexOf("|")
    const barcodeWithoutLastPipe = barcode.substring(0, lastPipeIndex)

    if (actionIds.includes(barcodeWithoutLastPipe)) {
      return { isValid: true, correctedBarcode: barcodeWithoutLastPipe }
    }

    // Strip again: Remove the last pipe (from already shortened string) and everything after it, then compare
    const secondLastPipeIndex = barcodeWithoutLastPipe.lastIndexOf("|")
    if (secondLastPipeIndex !== -1) {
      const barcodeWithoutSecondLastPipe = barcodeWithoutLastPipe.substring(0, secondLastPipeIndex)
      if (actionIds.includes(barcodeWithoutSecondLastPipe)) {
        return { isValid: true, correctedBarcode: barcodeWithoutSecondLastPipe }
      }
    }

    // If none of the attempts found a match, it's invalid
    return { isValid: false, correctedBarcode: barcode }
  }

  if (pipeCount === 1) {
    // Check full barcode first
    if (actionIds.includes(barcode)) {
      return { isValid: true, correctedBarcode: barcode }
    }

    // Remove last pipe and everything after it
    const lastPipeIndex = barcode.lastIndexOf("|")
    const barcodeWithoutLastPipe = barcode.substring(0, lastPipeIndex)

    if (actionIds.includes(barcodeWithoutLastPipe)) {
      return { isValid: true, correctedBarcode: barcodeWithoutLastPipe }
    }

    // If not found, it's invalid
    return { isValid: false, correctedBarcode: barcode }
  }

  // If none of the rules match, it's invalid
  return { isValid: false, correctedBarcode: barcode }
}
